// Frontend error handling and logging utilities

export type ErrorType = 
  | 'NETWORK_ERROR'
  | 'WEBSOCKET_ERROR' 
  | 'AUDIO_ERROR'
  | 'PERMISSION_ERROR'
  | 'FORMAT_ERROR'
  | 'VALIDATION_ERROR'
  | 'COMPONENT_ERROR'
  | 'UNKNOWN_ERROR';

export interface ErrorContext {
  component?: string;
  operation?: string;
  roomId?: string;
  connectionId?: string;
  metadata?: Record<string, unknown>;
}

export interface ErrorReport {
  type: ErrorType;
  message: string;
  stack?: string | undefined;
  timestamp: string;
  url: string;
  userAgent: string;
  context?: ErrorContext | undefined;
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorQueue: ErrorReport[] = [];
  private readonly MAX_QUEUE_SIZE = 100;

  private constructor() {
    // Set up global error handlers
    this.setupGlobalErrorHandlers();
  }

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  private setupGlobalErrorHandlers(): void {
    // Handle unhandled JavaScript errors
    window.addEventListener('error', (event) => {
      this.handleError(event.error || new Error(event.message), 'UNKNOWN_ERROR', {
        component: 'global',
        operation: 'unhandled_error',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError(
        event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
        'UNKNOWN_ERROR',
        {
          component: 'global',
          operation: 'unhandled_promise_rejection'
        }
      );
    });
  }

  handleError(error: Error | string, type: ErrorType, context?: ErrorContext): void {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    
    const report: ErrorReport = {
      type,
      message: errorObj.message,
      stack: errorObj.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      context
    };

    // Add to queue
    this.errorQueue.push(report);
    if (this.errorQueue.length > this.MAX_QUEUE_SIZE) {
      this.errorQueue.shift();
    }

    // Log to console
    this.logToConsole(report);

    // In a real application, you would send this to an error reporting service
    this.sendToErrorService(report);
  }

  private logToConsole(report: ErrorReport): void {
    const logLevel = this.getLogLevel(report.type);
    const message = `[${report.timestamp}] ${report.type}: ${report.message}`;
    
    if (report.context) {
      console[logLevel](message, report.context);
    } else {
      console[logLevel](message);
    }

    if (report.stack && logLevel === 'error') {
      console.error('Stack trace:', report.stack);
    }
  }

  private getLogLevel(type: ErrorType): 'error' | 'warn' | 'info' {
    switch (type) {
      case 'NETWORK_ERROR':
      case 'WEBSOCKET_ERROR':
      case 'COMPONENT_ERROR':
      case 'UNKNOWN_ERROR':
        return 'error';
      case 'AUDIO_ERROR':
      case 'PERMISSION_ERROR':
      case 'FORMAT_ERROR':
        return 'warn';
      case 'VALIDATION_ERROR':
        return 'info';
      default:
        return 'error';
    }
  }

  private async sendToErrorService(report: ErrorReport): Promise<void> {
    try {
      // In a real application, you would send this to your error reporting service
      // For example: Sentry, LogRocket, Bugsnag, etc.
      
      // For now, we'll just simulate sending to a service
      console.log('Sending error report to service:', report);
      
      // Example implementation:
      // await fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(report)
      // });
    } catch (error) {
      console.error('Failed to send error report:', error);
    }
  }

  getRecentErrors(limit: number = 10): ErrorReport[] {
    return this.errorQueue.slice(-limit);
  }

  clearErrors(): void {
    this.errorQueue = [];
  }

  // Specific error handling methods
  handleNetworkError(error: Error, context?: ErrorContext): void {
    this.handleError(error, 'NETWORK_ERROR', {
      ...context,
      operation: context?.operation || 'network_request'
    });
  }

  handleWebSocketError(error: Error, context?: ErrorContext): void {
    this.handleError(error, 'WEBSOCKET_ERROR', {
      ...context,
      operation: context?.operation || 'websocket_operation'
    });
  }

  handleAudioError(error: Error, context?: ErrorContext): void {
    this.handleError(error, 'AUDIO_ERROR', {
      ...context,
      operation: context?.operation || 'audio_operation'
    });
  }

  handlePermissionError(error: Error, context?: ErrorContext): void {
    this.handleError(error, 'PERMISSION_ERROR', {
      ...context,
      operation: context?.operation || 'permission_request'
    });
  }

  handleValidationError(message: string, context?: ErrorContext): void {
    this.handleError(new Error(message), 'VALIDATION_ERROR', {
      ...context,
      operation: context?.operation || 'validation'
    });
  }
}

// User-friendly error messages
export const ERROR_MESSAGES: Record<ErrorType, { title: string; description: string; suggestions: string[] }> = {
  NETWORK_ERROR: {
    title: 'Connection Problem',
    description: 'Unable to connect to the server. Please check your internet connection.',
    suggestions: [
      'Check your internet connection',
      'Try refreshing the page',
      'Wait a moment and try again'
    ]
  },
  WEBSOCKET_ERROR: {
    title: 'Real-time Connection Lost',
    description: 'The live connection was interrupted. Attempting to reconnect...',
    suggestions: [
      'Wait for automatic reconnection',
      'Check your internet connection',
      'Refresh the page if problems persist'
    ]
  },
  AUDIO_ERROR: {
    title: 'Audio System Error',
    description: 'There was a problem with the audio system.',
    suggestions: [
      'Check microphone permissions',
      'Ensure your microphone is connected',
      'Try using a different browser',
      'Check if other applications are using the microphone'
    ]
  },
  PERMISSION_ERROR: {
    title: 'Permission Required',
    description: 'The application needs permission to access your microphone.',
    suggestions: [
      'Click "Allow" when prompted for microphone access',
      'Check browser settings for microphone permissions',
      'Reload the page and try again'
    ]
  },
  FORMAT_ERROR: {
    title: 'Format Not Supported',
    description: 'Your browser doesn\'t support the required audio format (WebM/Opus).',
    suggestions: [
      'Try using Chrome, Firefox, or Edge',
      'Update your browser to the latest version',
      'Check if WebM/Opus is supported in your browser'
    ]
  },
  VALIDATION_ERROR: {
    title: 'Invalid Input',
    description: 'The provided information is not valid.',
    suggestions: [
      'Check the format of your input',
      'Ensure all required fields are filled',
      'Try again with different values'
    ]
  },
  COMPONENT_ERROR: {
    title: 'Application Error',
    description: 'A component in the application encountered an error.',
    suggestions: [
      'Try refreshing the page',
      'Clear your browser cache',
      'Contact support if the problem persists'
    ]
  },
  UNKNOWN_ERROR: {
    title: 'Unexpected Error',
    description: 'An unexpected error occurred.',
    suggestions: [
      'Try refreshing the page',
      'Clear your browser cache',
      'Try using a different browser',
      'Contact support if the problem persists'
    ]
  }
};

// Utility functions
export function getErrorMessage(type: ErrorType): { title: string; description: string; suggestions: string[] } {
  return ERROR_MESSAGES[type] || ERROR_MESSAGES.UNKNOWN_ERROR;
}

export function formatErrorForUser(_error: Error | string, type: ErrorType): string {
  const errorMessage = getErrorMessage(type);
  return `${errorMessage.title}: ${errorMessage.description}`;
}

// Performance monitoring
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: Map<string, number[]> = new Map();

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  startTimer(operation: string): () => number {
    const startTime = performance.now();
    
    return () => {
      const duration = performance.now() - startTime;
      this.recordMetric(operation, duration);
      return duration;
    };
  }

  recordMetric(operation: string, duration: number): void {
    if (!this.metrics.has(operation)) {
      this.metrics.set(operation, []);
    }
    
    const operationMetrics = this.metrics.get(operation)!;
    operationMetrics.push(duration);
    
    // Keep only the last 100 measurements
    if (operationMetrics.length > 100) {
      operationMetrics.shift();
    }
  }

  getMetrics(operation: string): { average: number; min: number; max: number; count: number } | null {
    const operationMetrics = this.metrics.get(operation);
    if (!operationMetrics || operationMetrics.length === 0) {
      return null;
    }

    const sum = operationMetrics.reduce((a, b) => a + b, 0);
    return {
      average: sum / operationMetrics.length,
      min: Math.min(...operationMetrics),
      max: Math.max(...operationMetrics),
      count: operationMetrics.length
    };
  }

  getAllMetrics(): Record<string, { average: number; min: number; max: number; count: number }> {
    const result: Record<string, { average: number; min: number; max: number; count: number }> = {};
    
    for (const [operation, metrics] of this.metrics.entries()) {
      if (metrics.length > 0) {
        const sum = metrics.reduce((a, b) => a + b, 0);
        result[operation] = {
          average: sum / metrics.length,
          min: Math.min(...metrics),
          max: Math.max(...metrics),
          count: metrics.length
        };
      }
    }
    
    return result;
  }
}

// Global instances
export const errorHandler = ErrorHandler.getInstance();
export const performanceMonitor = PerformanceMonitor.getInstance();